import React, { useState, useEffect } from 'react'
import { Download, RefreshCw, FileText, Calendar, HardDrive } from 'lucide-react'
import axios from 'axios'
import clsx from 'clsx'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api-cluster.prod.crawler.ai.rockspoon.io/proxy-pilot/v1'

const Downloads = () => {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    totalFiles: 0,
    totalSize: 0,
    latestFile: null
  })

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  const truncateTaskId = (taskId) => {
    if (!taskId) return 'N/A'
    return taskId.length > 16 ? taskId.substring(0, 16) + '...' : taskId
  }

  const handleDownload = async (file) => {
    try {
      // Try downloading by task_id first
      if (file.task_id) {
        const taskResponse = await axios.get(`${API_BASE_URL}/api/downloads/download-url/${file.task_id}`)
        if (taskResponse.data.download_available) {
          window.open(`${API_BASE_URL}/api/downloads/download/${file.task_id}`, '_blank')
          return
        }
      }
      
      // Fallback to direct file download
      window.open(`${API_BASE_URL}/api/downloads/file/${file.filename}`, '_blank')
    } catch (error) {
      console.error('Download failed:', error)
      // Try direct file download as fallback
      window.open(`${API_BASE_URL}/api/downloads/file/${file.filename}`, '_blank')
    }
  }

  const loadFiles = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await axios.get(`${API_BASE_URL}/api/downloads/files`)
      const data = response.data

      if (data.files && data.files.length > 0) {
        setFiles(data.files)
        
        const totalSize = data.files.reduce((sum, file) => sum + file.size, 0)
        setStats({
          totalFiles: data.total_count,
          totalSize,
          latestFile: data.files[0]
        })
      } else {
        setFiles([])
        setStats({ totalFiles: 0, totalSize: 0, latestFile: null })
      }
    } catch (err) {
      setError(`Failed to load files: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFiles()
  }, [])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Download Center</h2>
          <p className="text-gray-600">Download crawling results in JSON format</p>
        </div>
        <button
          onClick={loadFiles}
          disabled={loading}
          className={clsx(
            "flex items-center space-x-2 px-4 py-2 rounded-md transition-colors",
            loading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          )}
        >
          <RefreshCw className={clsx("w-4 h-4", loading && "animate-spin")} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-600">Total Files</span>
          </div>
          <div className="text-2xl font-bold text-blue-900 mt-1">
            {stats.totalFiles}
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <HardDrive className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-600">Total Size</span>
          </div>
          <div className="text-2xl font-bold text-green-900 mt-1">
            {formatBytes(stats.totalSize)}
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-600">Latest File</span>
          </div>
          <div className="text-sm font-bold text-purple-900 mt-1">
            {stats.latestFile ? formatDate(stats.latestFile.created_at) : 'N/A'}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Files Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Task ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="text-gray-500">Loading files...</span>
                    </div>
                  </td>
                </tr>
              ) : files.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <div className="text-gray-500">No result files found</div>
                  </td>
                </tr>
              ) : (
                files.map((file, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {file.filename}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div 
                        className="text-sm text-gray-900" 
                        title={file.task_id || 'N/A'}
                      >
                        {truncateTaskId(file.task_id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatBytes(file.size)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(file.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDownload(file)}
                        className="inline-flex items-center text-blue-600 hover:text-blue-900 transition-colors cursor-pointer"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default Downloads
